// DeleteAccountPage — the public account-deletion instructions page.
//
// Exists because Google Play's Data Safety form requires a *web* URL where a
// user can find out how to delete their account and data, reachable WITHOUT
// signing in (a reviewer opens it signed out, from a desktop browser). It also
// serves as the URL we give Apple for guideline 5.1.1(v).
//
// Deliberately NOT gated behind auth and NOT a redirect into /terms — a
// reviewer must land on deletion instructions, not mid-way down a combined
// Terms & Privacy document. The substance mirrors the Privacy Policy's
// Deletion bullet; keep the two in sync if either changes.

export default function DeleteAccountPage() {
  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.logoLine}>COLLECTIONKEEPER.ORG · ACCOUNT</div>
        <h1 style={styles.title}>DELETE YOUR <span style={styles.accent}>ACCOUNT</span></h1>
        <div style={styles.subtitle}>Last updated: August 2026</div>
        <div style={styles.divider} />

        <section style={styles.section}>
          <h2 style={styles.heading}>Delete It Yourself — Takes Effect Immediately</h2>
          <p style={styles.text}>You can delete your Collection Keeper account and all of its data yourself, at any time, from the website or either mobile app. No email, no waiting period, no asking us.</p>
          <ol style={styles.list}>
            <li style={styles.listItem}>Sign in to Collection Keeper (website, iOS app, or Android app).</li>
            <li style={styles.listItem}>Open the menu and go to <strong style={styles.strong}>Edit Profile</strong>.</li>
            <li style={styles.listItem}>Scroll to the bottom and choose <strong style={styles.strong}>Delete My Account</strong>.</li>
            <li style={styles.listItem}>Confirm. Your account is deleted right away and you are signed out.</li>
          </ol>
          <p style={styles.text}>Tip: if you want to keep a copy of your collection, export it first from My Collection &rarr; Export. Once the account is deleted we cannot recover anything.</p>

          <h3 style={styles.subheading}>Or Ask Us</h3>
          <p style={styles.text}>If you cannot sign in, email <span style={styles.email}>keeper18@collectionkeeper.org</span> from the address on the account and ask us to delete it. We process these within 30 days.</p>
        </section>

        <div style={styles.divider} />

        <section style={styles.section}>
          <h2 style={styles.heading}>What Gets Deleted</h2>
          <p style={styles.text}>Deleting your account permanently removes:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Your login and email address</li>
            <li style={styles.listItem}>Your username, profile, bio and avatar</li>
            <li style={styles.listItem}>Your entire collection — collected cards, wishlist, grades, serial numbers and prices</li>
            <li style={styles.listItem}>Card photos you uploaded, and any Marketplace listing photos</li>
            <li style={styles.listItem}>Your Marketplace details — name, shipping address, phone number, PayPal email and Venmo handle</li>
            <li style={styles.listItem}>Your identity-verification status, if you ever verified for a high-value sale</li>
            <li style={styles.listItem}>Your messages and their attachments</li>
          </ul>

          <h3 style={styles.subheading}>What We Keep, With Your Name Removed</h3>
          <p style={styles.text}>A few records survive deletion, but your identity is stripped from them — they no longer name you, point at your account, or let anyone sign back in as you:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong style={styles.strong}>Records of completed sales</strong> — a marketplace sale involves another collector, so the record of what sold, for how much, and when is kept for fraud prevention and tax purposes, with the buyer and seller detached from it.</li>
            <li style={styles.listItem}><strong style={styles.strong}>Card-scanner corrections</strong> — if you corrected a scan result, the card details from that correction stay so scans keep improving for everyone. They contain no personal information and no photo.</li>
            <li style={styles.listItem}><strong style={styles.strong}>Sign-in and session counts</strong> — kept as anonymous activity totals so we can see how busy the site is, detached from any account.</li>
            <li style={styles.listItem}><strong style={styles.strong}>Feedback you sent us</strong> — the message stays so we can act on it; your name and email address are removed from it.</li>
          </ul>
          <p style={styles.text}>Card-scanner photos are never part of any of this: they are analyzed and discarded at scan time and were never stored in the first place.</p>
        </section>

        <div style={styles.divider} />

        <section style={styles.section}>
          <h2 style={styles.heading}>Questions</h2>
          <p style={styles.text}>Email <span style={styles.email}>keeper18@collectionkeeper.org</span>. Our full privacy policy, including everything we collect and every provider we use, is on the <a href="/terms" style={styles.link}>Terms &amp; Privacy</a> page.</p>
        </section>

        <div style={styles.divider} />
        <div style={styles.footer}>
          <a href="/" style={styles.backLink}>← Back to CollectionKeeper</a>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "var(--bg)", color: "var(--text)", padding: "60px 20px" },
  container: { maxWidth: "720px", margin: "0 auto" },
  logoLine: { fontFamily: "'Orbitron', monospace", fontSize: "9px", letterSpacing: "6px", color: "var(--brand-gold)", marginBottom: "12px", opacity: 0.8 },
  title: { fontFamily: "'Orbitron', monospace", fontSize: "32px", fontWeight: 900, color: "#fff", margin: "0 0 8px 0" },
  accent: { color: "var(--cyan)" },
  subtitle: { fontFamily: "'Share Tech Mono', monospace", fontSize: "11px", color: "var(--muted-2)", letterSpacing: "2px" },
  divider: { height: "1px", background: "linear-gradient(90deg, var(--brand-gold), var(--border), transparent)", margin: "32px 0" },
  section: { marginBottom: "8px" },
  heading: { fontFamily: "'Orbitron', monospace", fontSize: "16px", fontWeight: 700, color: "var(--cyan)", letterSpacing: "2px", marginBottom: "24px" },
  subheading: { fontFamily: "'Orbitron', monospace", fontSize: "11px", fontWeight: 600, color: "var(--brand-gold)", letterSpacing: "2px", marginTop: "24px", marginBottom: "8px" },
  text: { fontFamily: "'Share Tech Mono', monospace", fontSize: "14px", color: "var(--muted-8)", lineHeight: 1.8, margin: "0 0 8px 0" },
  list: { paddingLeft: "20px", margin: "8px 0" },
  listItem: { fontFamily: "'Share Tech Mono', monospace", fontSize: "14px", color: "var(--muted-8)", lineHeight: 2 },
  strong: { color: "var(--text)" },
  email: { color: "var(--cyan)" },
  link: { color: "var(--cyan)" },
  footer: { marginTop: "16px" },
  backLink: { fontFamily: "'Orbitron', monospace", fontSize: "11px", letterSpacing: "2px", color: "var(--muted)", textDecoration: "none" },
};
